import {
    Check,
    Code,
    Description,
    ExitToApp,
    Expand,
    OpenInFull,
    TableChart,
} from '@mui/icons-material';
import {
    AppBar,
    Box,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Features } from '~/components/FeatureToggle';
import { initialGoals } from '~/containers/viewer/table-panel/optimization-objectives';
import useDose from '~/hooks/patient/dose/useDose';
import {
    expandDoseStatisticsTableState,
    showDoseStatisticsTableFullScreenState,
} from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import {
    createJSONContent,
    createCSVContent,
    downloadCreatedFile,
} from '~/utils/io/files/handleFileExport';
import DoseStatisticsTable from './DoseStatisticsTable';
import OptimizationObjectivesComponent from './optimization-objectives';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { Dvh } from '~/domain/patient/dvh';
import { Roi } from '~/domain/dicom/dicomstructureset';
import { getDICOMDate } from '~/domain/dicom/utils';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { environmentState } from '~/state/atoms/engineAtoms';
import { LTTB } from 'downsample';

const TablePanel: React.FC = () => {
    const isOptimizationObjectivesFeatureEnabled = AppConfig.isFeatureEnabled(
        Features.OPTIMIZATION_OBJECTIVES
    );

    const { dose } = useDose();
    const selectedPatient = useSelectedPatient();

    const [expandDoseStatisticsTable, setExpandDoseStatisticsTable] =
        useRecoilState(expandDoseStatisticsTableState);
    const [
        showDoseStatisticsTableFullScreen,
        setShowDoseStatisticsTableFullScreen,
    ] = useRecoilState(showDoseStatisticsTableFullScreenState);

    const currentDose = useRecoilValue(currentDoseState);
    const env = useRecoilValue(environmentState);

    const [selectedComponent, setSelectedComponent] =
        useState<string>('doseStatistics');
    const [exportBtnAnchorEl, setExportBtnAnchorEl] =
        useState<HTMLButtonElement | null>(null);

    const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setExportBtnAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setExportBtnAnchorEl(null);
    };
    const getSelectedPatientDetails = () => {
        if (!selectedPatient?.dataSet || !currentDose || !currentDose.structureSet)
            return {
                patientId: '',
                doseUid: '',
                structureSetLabel: '',
            };

        return {
            patientId: selectedPatient.dataSet.PatientID,
            doseUid: currentDose.dicomUid,
            structureSetLabel: currentDose.structureSet.structureSetLabel,
        };
    };

const handleExportDoseStatsCsv = () => {
    const currentDate = getDICOMDate(new Date());
    const { patientId, doseUid, structureSetLabel} =
        getSelectedPatientDetails();

    if (selectedComponent === 'doseStatistics') {
        const doseHeaderRow = [
            'Structure',
            'Min Dose (Gy)',
            'Max Dose (Gy)',
            'Mean Dose (Gy)',
        ];
        const doseDataRows = (dose?.dvhs || [])
            .filter((dvh) => dvh.visible)
            .map((dvh) => [
                dvh.name,
                dvh.minDose.toFixed(3),
                dvh.maxDose.toFixed(3),
                dvh.meanDose.toFixed(3),
            ]);

        const summaryHeaderRow = [
            'PatientID',
            'StuctureSetLabel',
            'DoseUID',
        ];
        const summaryDataRows = [
            [patientId, structureSetLabel, doseUid],
        ];

        const doseSection = [doseHeaderRow, ...doseDataRows]
            .map((row) => row.join(','))
            .join('\n');

        const summarySection = [summaryHeaderRow, ...summaryDataRows]
            .map((row) => row.join(','))
            .join('\n');

        const csvContent = `${summarySection}\n\n${doseSection}`;
        const csvUri = createCSVContent('', [csvContent]);

        downloadCreatedFile(csvUri, `dose_statistics_${currentDate}.csv`);
    } else if (selectedComponent === 'optimizationObjectives') {
        const objectivesHeaderRow = [
            'Structure',
            'Volume %',
            'Dose',
            'Priority',
            'gEUD',
        ];
        const objectivesDataRows = initialGoals.map((goal) => [
            goal.structure,
            goal.volumePrecent,
            goal.dose,
            goal.priority,
            goal.gEUD,
        ]);

        const summaryHeaderRow = [
            'PatientID',
            'StuctureSetLabel',
            'DoseUID',
        ];
        const summaryDataRows = [
            [patientId, structureSetLabel, doseUid],
        ];

        const objectivesSection = [
            objectivesHeaderRow,
            ...objectivesDataRows,
        ]
            .map((row) => row.join(','))
            .join('\n');

        const summarySection = [summaryHeaderRow, ...summaryDataRows]
            .map((row) => row.join(','))
            .join('\n');

        const csvContent = `${summarySection}\n\n${objectivesSection}`;
        const csvUri = createCSVContent('', [csvContent]);

        downloadCreatedFile(csvUri, `optimization_objectives_${currentDate}.csv`);
    }

    handleMenuClose();
};

const handleExportDoseStatsJson = () => {
    const currentDate = getDICOMDate(new Date()); 

    if (selectedComponent === 'doseStatistics') {
        const jsonData = dose?.dvhs
            .filter((dvh) => dvh.visible)
            .map((dvh) => ({
                structure: dvh.name,
                minDose: dvh.minDose.toFixed(3),
                maxDose: dvh.maxDose.toFixed(3),
                meanDose: dvh.meanDose.toFixed(3),
            }));

        const jsonUri = createJSONContent(jsonData || []);
        downloadCreatedFile(jsonUri, `dose_statistics_${currentDate}.json`);
    } else if (selectedComponent === 'optimizationObjectives') {
        const jsonData = initialGoals.map((goal) => ({
            structure: goal.structure,
            volumePrecent: goal.volumePrecent,
            priority: goal.priority,
            dose: goal.dose,
            gEUD: goal.gEUD,
        }));

        const jsonUri = createJSONContent(jsonData);
        downloadCreatedFile(jsonUri, `optimization_objectives_${currentDate}.json`);
    }

    handleMenuClose();
};


    const handleExportDvhCsv = () => {
        const dvhs: Map<Roi, Dvh> | undefined = currentDose?.getDvhs();
        if (!dvhs || dvhs.size === 0) {
            console.warn('No DVH data available to export.');
            return;
        }
        const currentDate = getDICOMDate(new Date());
        const globalMetadata = [
            `Patient Name: ${
                selectedPatient?.dataSet.PatientName || 'Unknown'
            }`,
            `Patient ID: ${selectedPatient?.dataSet.PatientID || 'Unknown'}`,
            `Plan: ${dose?.planLabel || 'Unknown'}`,
            `Comment: ${dose?.doseComment || 'Unknown'}`,
            `Export Date: ${currentDate}`,
            ``,
        ].join('\n');
        const doseHeader =
            'Dose [Gy], Relative Dose [%], Ratio of Total Structure Volume [%]';
        const doseDataRows = Array.from(dvhs.entries()).map(
            ([structure, dvh]) => {
                const roiVolume =
                    env?.getRoiVolumeCm3(structure.roiNumber) ?? Number.NaN;
                const roiMetadata = [
                    `Structure: ${structure?.name}`,
                    `Approval Status: ${
                        structure?.approvalStatus || 'Unknown'
                    }`,
                    `Volume [cm³]: ${roiVolume?.toFixed(3)}`,
                    `Min Dose [Gy]: ${dvh.minDose.toFixed(3)}`,
                    `Max Dose [Gy]: ${dvh.maxDose.toFixed(3)}`,
                    `Mean Dose [Gy]: ${dvh.meanDose.toFixed(3)}`,
                    `Equiv. Sphere Diam. [cm]: ${(
                        2 * Math.cbrt((3 * roiVolume) / (4 * Math.PI))
                    ).toFixed(3)}`,
                    ``,
                ].join('\n');
                const originalData = dvh.dvhData.map((volume, index) => ({
                    x: index * dvh.binWidth,
                    y: volume,
                }));
                const downsampledData = LTTB(originalData, 1000) as {
                    x: number;
                    y: number;
                }[];
                const doseRows: string[] = [];
                downsampledData.forEach((point) => {
                    if (point.x >= 0 && dose?.normalizationValueGy) {
                        const relativeDose =
                            (100 * point.x) / dose?.normalizationValueGy;
                        doseRows.push(
                            `${point.x.toFixed(3)}, ${relativeDose.toFixed(
                                3
                            )}, ${point.y.toFixed(3)}`
                        );
                    }
                });
                return `${roiMetadata}\n${doseHeader}\n${doseRows.join('\n')}`;
            }
        );
        const csvContent = `${globalMetadata}\n${doseDataRows.join('\n\n')}`;
        const csvUri = createCSVContent('', [csvContent]);
        downloadCreatedFile(csvUri, `dvh_export_${currentDate}.csv`);
        handleMenuClose();
    };

    const renderComponent = () => {
        switch (selectedComponent) {
            case 'doseStatistics':
                return <DoseStatisticsTable dose={dose} />;
            case 'optimizationObjectives':
                return <OptimizationObjectivesComponent />;
            default:
                return null;
        }
    };

    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: string
    ) => {
        setSelectedComponent(newValue);
    };

    const renderButtons = () => (
        <React.Fragment>
            <Tooltip title="Export" placement="top">
                <IconButton color="secondary" onClick={handleExportClick}>
                    <ExitToApp />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={exportBtnAnchorEl}
                open={Boolean(exportBtnAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: expandDoseStatisticsTable ? 'top' : 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: expandDoseStatisticsTable ? 'bottom' : 'top',
                    horizontal: 'left',
                }}
            >
                <MenuItem onClick={handleExportDvhCsv} sx={{ py: 1 }}>
                    <Description fontSize="small" style={{ marginRight: 8 }} />
                    <Typography fontSize={13}>Export DVH as CSV</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleExportDoseStatsCsv} sx={{ py: 1 }}>
                    <Description fontSize="small" style={{ marginRight: 8 }} />
                    <Typography fontSize={13}>
                        Export Dose Statistics as CSV
                    </Typography>
                </MenuItem>
                <MenuItem onClick={handleExportDoseStatsJson} sx={{ py: 1 }}>
                    <Code fontSize="small" style={{ marginRight: 8 }} />
                    <Typography fontSize={13}>
                        Export Dose Statistics as JSON
                    </Typography>
                </MenuItem>
            </Menu>

            <Tooltip title="Resize" placement="top">
                <IconButton
                    color="primary"
                    onClick={() =>
                        showDoseStatisticsTableFullScreen
                            ? setShowDoseStatisticsTableFullScreen(false)
                            : setExpandDoseStatisticsTable(
                                  !expandDoseStatisticsTable
                              )
                    }
                >
                    <Expand />
                </IconButton>
            </Tooltip>

            <Tooltip title="Maximize" placement="top">
                <IconButton
                    color="primary"
                    onClick={() => {
                        if (expandDoseStatisticsTable) {
                            setExpandDoseStatisticsTable(false);
                            setShowDoseStatisticsTableFullScreen(true);
                        } else {
                            setShowDoseStatisticsTableFullScreen(
                                !showDoseStatisticsTableFullScreen
                            );
                        }
                    }}
                >
                    <OpenInFull />
                </IconButton>
            </Tooltip>
        </React.Fragment>
    );

    return (
        <Box sx={{ width: '100%' }}>
            <AppBar position="static" sx={{ height: '55px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <Tabs
                        value={selectedComponent}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="inherit"
                        sx={{ height: '100%' }}
                    >
                        <Tab
                            label="Dose Statistics"
                            value="doseStatistics"
                            icon={<TableChart />}
                            iconPosition="start"
                            sx={{ height: '55px', minHeight: '55px' }}
                        />
                        {isOptimizationObjectivesFeatureEnabled && (
                            <Tab
                                label="Optimization Objectives"
                                value="optimizationObjectives"
                                icon={<Check />}
                                iconPosition="start"
                                sx={{ height: '55px', minHeight: '55px' }}
                                disabled={
                                    !isOptimizationObjectivesFeatureEnabled
                                }
                            />
                        )}
                    </Tabs>
                    <Box sx={{ display: 'flex', gap: 1, pr: 2 }}>
                        {renderButtons()}
                    </Box>
                </Box>
            </AppBar>
            <Box sx={{ width: '100%', minHeight: 180 }}>
                {renderComponent()}
            </Box>
        </Box>
    );
};

export default TablePanel;
